import { useRef, useState } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { primary_color } from '../../../Helper/uiHelper'
import { request } from '../../../Redux/Sagas/requests/api'
import { useDispatch } from 'react-redux'
import { setNotification } from '../../../Redux/Reducers/notificationHandling'

/**
 * Blocks grid row button — same idea as purchase SentConfirmationEmail,
 * but confirms first because it can email every paid booking in the block.
 */
const SendBlockConfirmationEmails = ({ row }) => {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [countLoading, setCountLoading] = useState(false)
  const [count, setCount] = useState(null)
  const [blockName, setBlockName] = useState(row?.original?.name || '')
  const btnRef = useRef(null)

  const locationId = row?.original?.id
  if (!locationId) return null

  const showTooltip = isOpen && !modalOpen && btnRef.current
  const rect = btnRef.current?.getBoundingClientRect?.()

  const openConfirm = () => {
    setIsOpen(false)
    setModalOpen(true)
    setCount(null)
    setCountLoading(true)
    request('purchase/send-block-confirmation-emails', 'POST', {
      location_id: locationId,
      dry_run: true,
    }).then((res) => {
      setCountLoading(false)
      if (res?.status === 200 || res?.status === 202) {
        setCount(res?.data?.count ?? 0)
        setBlockName(res?.data?.block_name || row?.original?.name || '')
      } else {
        dispatch(
          setNotification({
            type: 'error',
            message: res?.data?.message || 'Could not count bookings for this block.',
          })
        )
        setModalOpen(false)
      }
    })
  }

  const onSend = () => {
    setLoading(true)
    request('purchase/send-block-confirmation-emails', 'POST', {
      location_id: locationId,
    }).then((res) => {
      setLoading(false)
      setModalOpen(false)
      if (res?.status === 200 || res?.status === 202) {
        dispatch(
          setNotification({
            type: 'success',
            message: res?.data?.message || `Sent ${res?.data?.sent ?? 0} confirmation email(s).`,
          })
        )
      } else {
        dispatch(
          setNotification({
            type: 'error',
            message: res?.data?.message || 'Failed to send confirmation emails.',
          })
        )
      }
    })
  }

  return (
    <div>
      <Button
        innerRef={btnRef}
        className='subtitle'
        style={{ background: primary_color, border: 'none' }}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        onClick={(e) => {
          e.stopPropagation()
          openConfirm()
        }}>
        {loading ? <i className='fa-solid fa-spinner fa-spin'></i> : <i className='fa-solid fa-envelope'></i>}
      </Button>

      {showTooltip && rect && (
        <div
          style={{
            position: 'fixed',
            top: rect.bottom + 5,
            left: rect.left,
            background: '#333',
            color: '#fff',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            zIndex: 9999,
            whiteSpace: 'nowrap',
          }}>
          Send confirmation emails (block)
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        toggle={() => !loading && setModalOpen(false)}
        centered
        style={{ boxShadow: 'none' }}
        onClick={(e) => e.stopPropagation()}>
        <ModalHeader toggle={() => !loading && setModalOpen(false)}>Send confirmation emails</ModalHeader>
        <ModalBody>
          {countLoading ? (
            <div>
              <i className='fa-solid fa-spinner fa-spin mr-2'></i>
              Counting paid bookings in this block…
            </div>
          ) : (
            <div>
              Send confirmation emails to all paid bookings in block <strong>{blockName || locationId}</strong>?
              <div className='mt-2'>
                Recipients: <strong>{count ?? 0}</strong> payment(s)
              </div>
              {count === 0 ? (
                <div className='mt-2 text-muted'>There are no paid bookings in this block.</div>
              ) : (
                <div className='mt-2 text-muted'>Same confirmation email as Purchase resend.</div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' disabled={loading} onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button
            color='primary'
            style={{ background: primary_color, border: 'none' }}
            disabled={loading || countLoading || count === 0}
            onClick={onSend}>
            {loading ? (
              <>
                <i className='fa-solid fa-spinner fa-spin mr-2'></i>
                Sending…
              </>
            ) : (
              'Yes, send emails'
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default SendBlockConfirmationEmails
